/**
 * MessageSettings — configure tone, rate limits, and follow-up timing.
 * Fetches current config on mount and saves via PUT /api/settings/messages.
 */

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SafetyScore } from '@/components/safety-score'
import { FieldHint } from '@/components/field-hint'
import { useApi } from '@/hooks/use-api'
import type { MessageConfig, MessageTone } from '@dealcloser/shared'
import { DEFAULT_MESSAGES } from '@dealcloser/shared'

const messageKeys = { config: ['settings', 'messages'] as const }

const TONE_OPTIONS: readonly { readonly value: MessageTone; readonly label: string }[] = [
  { value: 'professional_casual', label: 'Professional Casual' },
  { value: 'casual', label: 'Casual' },
  { value: 'professional', label: 'Professional' },
  { value: 'formal', label: 'Formal' },
] as const

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none'

export default function MessageSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<MessageConfig>(DEFAULT_MESSAGES)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: messageKeys.config,
    queryFn: async (): Promise<MessageConfig> => {
      const res = await apiFetch<MessageConfig>('/api/settings/messages')
      if (!res.success || !res.data) return DEFAULT_MESSAGES
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: MessageConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/messages', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messageKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  /** Parse a number input, clamping to [min, max]. Returns min on NaN. */
  function parseNum(raw: string, min: number, max: number): number {
    const n = parseInt(raw, 10)
    if (Number.isNaN(n)) return min
    return Math.min(Math.max(n, min), max)
  }

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  return (
    <div className="space-y-6">
      {/* Tone & Style */}
      <Card>
        <CardHeader>
          <CardTitle>Tone & Style</CardTitle>
          <CardDescription>Control how the AI writes messages on your behalf.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-tone">Message Tone</Label>
              <FieldHint label="How does message tone work?">
                Sets the overall register the AI writes in. Casual reads like a friendly DM; formal leans more polished. Doesn't override your voice samples, just biases the baseline.
              </FieldHint>
            </div>
            <Select
              value={form.messageTone}
              onValueChange={(value: MessageTone) =>
                setForm((prev) => ({ ...prev, messageTone: value }))
              }
              disabled={isLoading || isSaving}
            >
              <SelectTrigger id="msg-tone">
                <SelectValue placeholder="Select tone" />
              </SelectTrigger>
              <SelectContent>
                {TONE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-example">Example Message</Label>
              <FieldHint label="How is the example message used?">
                A reference DM in your own voice. The AI mirrors its cadence, vocabulary, and rhythm, never copies it verbatim. One good example beats a long instruction.
              </FieldHint>
            </div>
            <textarea
              id="msg-example"
              rows={4}
              placeholder="Paste a message that represents your ideal outreach style..."
              value={form.exampleMessage}
              onChange={(e) => setForm((prev) => ({ ...prev, exampleMessage: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-instructions">Custom AI Instructions</Label>
              <FieldHint label="What are custom AI instructions?">
                Extra do's and don'ts injected into every generation prompt. Use this for things like "don't mention competitors by name" or "always reference our Series B raise". Short, specific lines work best.
              </FieldHint>
            </div>
            <textarea
              id="msg-instructions"
              rows={3}
              placeholder="Any special instructions for the AI when generating messages..."
              value={form.customInstructions}
              onChange={(e) => setForm((prev) => ({ ...prev, customInstructions: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>
        </CardContent>
      </Card>

      {/* Rate Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Limits</CardTitle>
          <CardDescription>Control outreach volume to keep your LinkedIn account safe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="msg-per-hour">Messages Per Hour</Label>
                <FieldHint label="How does messages-per-hour work?">
                  Max DMs sent per hour from this account. Keeps your activity inside human-looking limits so LinkedIn doesn't flag you. Lower is safer, especially on new accounts.
                </FieldHint>
              </div>
              <Input
                id="msg-per-hour"
                type="number"
                min={1}
                max={15}
                value={form.messagesPerHour}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    messagesPerHour: parseNum(e.target.value, 1, 15),
                  }))
                }
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="msg-daily-max">Max Daily Messages</Label>
                <FieldHint label="How does the daily cap work?">
                  Hard ceiling on total DMs sent per day across all campaigns. LinkedIn's organic limit is around 80-100 for established accounts; we recommend staying well under.
                </FieldHint>
              </div>
              <Input
                id="msg-daily-max"
                type="number"
                min={10}
                max={80}
                value={form.maxDailyMessages}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    maxDailyMessages: parseNum(e.target.value, 10, 80),
                  }))
                }
                disabled={isLoading || isSaving}
              />
            </div>
          </div>

          <SafetyScore
            messagesPerHour={form.messagesPerHour}
            maxDailyMessages={form.maxDailyMessages}
          />
        </CardContent>
      </Card>

      {/* Follow-Up Timing */}
      <Card>
        <CardHeader>
          <CardTitle>Follow-Up Timing</CardTitle>
          <CardDescription>Set the delay between each follow-up message.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="msg-fu1">Follow-Up 1 (days after initial)</Label>
                <FieldHint label="When does Follow-Up 1 fire?">
                  Days to wait after the first DM before sending FU1. FU1 is a light value drop, never a pitch. 3-4 days is the sweet spot — long enough to feel patient, short enough to stay top of mind.
                </FieldHint>
              </div>
              <Input
                id="msg-fu1"
                type="number"
                min={1}
                max={30}
                value={form.followUp1Days}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    followUp1Days: parseNum(e.target.value, 1, 30),
                  }))
                }
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="msg-fu2">Follow-Up 2 (days after FU1)</Label>
                <FieldHint label="When does Follow-Up 2 fire?">
                  Days after FU1 before FU2 ships. FU2 is the first real pitch reveal — names the product, drops one proof point, and asks for the meeting. Default 3-4 days.
                </FieldHint>
              </div>
              <Input
                id="msg-fu2"
                type="number"
                min={1}
                max={30}
                value={form.followUp2Days}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    followUp2Days: parseNum(e.target.value, 1, 30),
                  }))
                }
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="msg-fu3">Follow-Up 3 (days after FU2)</Label>
                <FieldHint label="When does Follow-Up 3 fire?">
                  Days after FU2 before FU3, the last directive touch. After FU3 the breakup message ships. 2-3 days here keeps the close window tight.
                </FieldHint>
              </div>
              <Input
                id="msg-fu3"
                type="number"
                min={1}
                max={30}
                value={form.followUp3Days}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    followUp3Days: parseNum(e.target.value, 1, 30),
                  }))
                }
                disabled={isLoading || isSaving}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Message Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
}
